import React from 'react';
import { useShop } from '../../context/ShopContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Minus, Plus, Trash2, ShoppingBag, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const CartDrawer = () => {
  const { isCartOpen, closeCart, cart, updateQuantity, removeFromCart, cartTotal, isCheckingOut, proceedToCheckout } = useShop();

  return (
    <Sheet open={isCartOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:w-[450px] flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="flex items-center gap-2 font-serif text-2xl text-primary">
            <ShoppingBag className="h-6 w-6" /> Handlekurv
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
              <ShoppingBag className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg">Handlekurven er tom</p>
              <Button variant="link" onClick={closeCart} className="text-primary mt-2">
                Begynn å handle
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <div className="h-24 w-24 rounded-lg overflow-hidden border bg-secondary/20 shrink-0">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <Link to={`/product/${item.handle || item.id}`} onClick={closeCart} className="hover:underline">
                        <h4 className="font-semibold text-foreground line-clamp-1">{item.name}</h4>
                      </Link>
                      {item.scientific && <p className="text-sm text-muted-foreground">{item.scientific}</p>}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 border rounded-md p-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.id, -1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm w-4 text-center">{item.quantity}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.id, 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="font-semibold">kr {item.price * item.quantity},-</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive self-start"
                    onClick={() => removeFromCart(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {cart.length > 0 && (
          <div className="border-t p-6 bg-secondary/10 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Frakt</span>
                <span>Beregnes i kassen</span>
              </div>
              <Separator />
              <div className="flex justify-between font-serif text-xl font-bold text-primary pt-2">
                <span>Totalt</span>
                <span>kr {cartTotal},-</span>
              </div>
              <p className="text-xs text-muted-foreground text-center pt-1">Inkl. mva</p>
            </div>
            <Button
              className="w-full h-12 text-lg rounded-full bg-primary hover:bg-primary/90 text-white shadow-md"
              onClick={proceedToCheckout}
              disabled={isCheckingOut}
            >
              {isCheckingOut ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Laster kasse...</>
              ) : (
                'Gå til kassen'
              )}
            </Button>
            <Button variant="outline" className="w-full rounded-full" onClick={closeCart}>
              Fortsett å handle
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
